/**
 * Календарь диапазона: сетка месяца, два клика — окно.
 *
 * Копия `supply/web/src/components/RangeCalendar.tsx` (30.09.26, для периода
 * P&L); история ниже — оттуда.
 *
 * Выделен из `DateRangePicker` 16.09.26 (замечание владельца разметкой на
 * `/supplier/3651`: «в периоде выбрано два поля по датам — используй нормальный
 * дейтпикер»). До этого дня календарь был только у «Планов», а окно продаж и
 * порядок листания правились парой `input type="date"`: их попап браузер рисует
 * СИСТЕМНЫМ окном — в тёмной теме белым, ровно та беда, из-за которой списки
 * отбора ушли на `Combo`.
 *
 * СВОЕГО ПОПАПА У КОМПОНЕНТА НЕТ — он рисует только календарь. Попап, кнопка и
 * список готовых окон остаются у того, кто его открыл: у «Планов» окна плановой
 * даты («с сегодня», «этот месяц»), у периода продаж — «30 дней», «60», «год».
 * Один календарь, две разные кнопки.
 *
 * СОСТОЯНИЕ ВЫБОРА ЖИВЁТ ЗДЕСЬ и умирает вместе с попапом: родитель рисует
 * календарь только открытым, поэтому начатый и брошенный выбор не переживает
 * закрытия — как и было, пока состояние лежало в `DateRangePicker`.
 *
 * Даты — строками `YYYY-MM-DD` через `lib/dates.ts`: сравнение строк такого
 * вида и есть сравнение дат, а `new Date("YYYY-MM-DD")` уносит день в полночь
 * UTC.
 */

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

import { iso, plusDays } from "@/lib/dates";

const MONTHS = [
  "январь", "февраль", "март", "апрель", "май", "июнь",
  "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь",
];
const WEEKDAYS = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];

/** Первое число месяца даты — срезами, без `new Date(строка)`. */
function месяцИз(value: string): Date {
  return new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, 1);
}

/** Шесть недель с понедельника: высота сетки одна у любого месяца, попап не прыгает. */
function сетка(месяц: Date): Date[] {
  const сдвиг = (месяц.getDay() + 6) % 7;
  const старт = plusDays(месяц, -сдвиг);
  return Array.from({ length: 42 }, (_, i) => plusDays(старт, i));
}

/** «24.09», а у чужого года «24.09.25»: окно бывает и прошлогодним. */
function краткая(value: string): string {
  const d = value.slice(8, 10);
  const m = value.slice(5, 7);
  const y = value.slice(0, 4);
  return y === String(new Date().getFullYear()) ? `${d}.${m}` : `${d}.${m}.${y.slice(2)}`;
}

export function RangeCalendar({
  from,
  to,
  onPick,
  single,
  max,
}: {
  /** Начало включительно, `YYYY-MM-DD`; пустая строка — без нижней границы. */
  from: string;
  /** Конец включительно; пустая строка — без верхней границы. */
  to: string;
  /** Вызывается ВТОРЫМ кликом — правка применяется сразу, без кнопки
   *  «применить»: черновик рядом с настоящим отбором был бы вторым состоянием. */
  onPick: (from: string, to: string) => void;
  /** Выбор ОДНОГО дня: клик отдаёт его обеими границами, второго не ждём. */
  single?: boolean;
  /** Верхняя граница включительно, `YYYY-MM-DD`; без нужды (плановая дата
   *  «Планов» смотрит в будущее) её не задают, и выбор ничем не ограничен. */
  max?: string | undefined;
}) {
  // Календарь открывается на месяце отбора, а не там, где его листали в прошлый
  // раз: вопрос «какие даты выбраны» важнее памяти о прокрутке.
  const [месяц, setМесяц] = useState(() => месяцИз(from || to || iso(new Date())));
  const [начало, setНачало] = useState<string | null>(null);
  const [наведено, setНаведено] = useState<string | null>(null);

  const сегодня = iso(new Date());

  const день = (value: string) => {
    // Клик по дню за границей молчит: витрина не даёт заглянуть дальше вчера,
    // а второго счёта «какое сегодня» в браузере нет — только та дата, что
    // прислал сервер.
    if (max && value > max) return;
    // Режим ОДНОГО дня: первый же клик отдаёт день обеими границами. Нужен
    // «Планам» при переносе партий — там вопрос «на какое число», а не «за
    // какой срок», и второго клика человеку осмыслить нечем.
    if (single) {
      onPick(value, value);
      return;
    }
    if (начало === null) {
      setНачало(value);
      return;
    }
    // Клик раньше начала меняет границы местами: «сначала конец, потом начало»
    // — тот же диапазон, и отказывать в нём незачем.
    if (value < начало) onPick(value, начало);
    else onPick(начало, value);
  };

  // Что подсвечено: пока выбирают — от начала до наведённого дня, иначе
  // нынешний отбор. Открытая граница — пустая строка.
  let лево = from;
  let право = to;
  if (начало !== null) {
    const второй = наведено ?? начало;
    лево = второй < начало ? второй : начало;
    право = второй < начало ? начало : второй;
  }
  const задан = лево !== "" || право !== "";
  const вДиапазоне = (value: string) => задан && (!лево || value >= лево) && (!право || value <= право);
  const край = (value: string) => value === лево || value === право;

  const листать = (шаг: number) => setМесяц((m) => new Date(m.getFullYear(), m.getMonth() + шаг, 1));

  return (
    <div className="px-2 pt-1.5 pb-2" data-annot="календарь">
      <div className="mb-1 flex items-center justify-between">
        <button
          type="button"
          aria-label="предыдущий месяц"
          onClick={() => листать(-1)}
          className="grid size-7 place-items-center rounded-[var(--radius-ui)] text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <ChevronLeft size={14} />
        </button>
        <span className="text-[12.5px] font-medium text-fg">
          {MONTHS[месяц.getMonth()]} {месяц.getFullYear()}
        </span>
        <button
          type="button"
          aria-label="следующий месяц"
          onClick={() => листать(1)}
          className="grid size-7 place-items-center rounded-[var(--radius-ui)] text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      <div className="grid grid-cols-7 text-center text-[10.5px] text-fg-faint">
        {WEEKDAYS.map((w) => (
          <span key={w} className="py-1">
            {w}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7" onMouseLeave={() => setНаведено(null)}>
        {сетка(месяц).map((date) => {
          const value = iso(date);
          const свой = date.getMonth() === месяц.getMonth();
          const граница = край(value);
          const внутри = вДиапазоне(value);
          // За верхней границей — не серым фоном, а погашенной цифрой без
          // наведения: витрина не знает будущего, и клик по нему обязан
          // выглядеть недоступным, а не молча ничего не делать. Пустая строка —
          // та же «без границы», что у `from`/`to` этого компонента.
          const запрещён = max ? value > max : false;
          return (
            <button
              key={value}
              type="button"
              title={value}
              disabled={запрещён}
              onClick={() => день(value)}
              onMouseEnter={() => setНаведено(value)}
              className={
                "h-8 text-[12px] tabular-nums " +
                (запрещён
                  ? "cursor-default text-fg-faint/40"
                  : граница
                    ? "rounded-[var(--radius-ui)] bg-accent font-medium text-bg"
                    : внутри
                      ? "bg-accent-dim text-accent-text"
                      : свой
                        ? "text-fg hover:bg-bg-hover"
                        : "text-fg-faint hover:bg-bg-hover") +
                // Сегодня отмечено подчёркиванием, а не заливкой: заливка занята
                // границами диапазона, и два смысла одного цвета читались бы как один.
                (value === сегодня && !граница
                  ? " font-semibold underline decoration-accent underline-offset-4"
                  : "")
              }
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      <div className="pt-1.5 text-[11px] text-fg-faint">
        {single
          ? "клик — выбрать день"
          : начало !== null
            ? `с ${краткая(начало)} — выберите конец`
            : "клик — начало, второй клик — конец"}
      </div>
    </div>
  );
}
